module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h2', {
    staticClass: "h2",
    attrs: {
      "id": "ch01"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_22"
    }
  }), _c('strong', [_vm._v("1"), _c('br'), _vm._v("Before the “Caucasian Race”"), _c('br'), _c('em', [_vm._v("Antecedents of European Racialism, ca. 1000–1684")])])]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("Naming occurs in sites, particular places, and at particular times. For a name to do its creative work, it needs authority. One needs usage within institutions. Naming does its work only as a social history works itself out.")]), _vm._v(" "), _c('p', {
    staticClass: "right"
  }, [_vm._v("—Ian Hacking"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en1"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1")])])]), _vm._v(" "), _c('p', {
    staticClass: "indenta"
  }, [_vm._v("There was no notion of a Caucasian race in the years between 1000 to 1684. In fact, the “race” concept itself was introduced by Europeans elites only near the end of this period, in the seventeenth century, after the rise of the Atlantic slave trade and massive enslavement of “black” Africans. Nevertheless, the ethnic history of Europe during this period, which stretched from the Middle Ages to the Enlightenment, was a prelude to the invention of the “Caucasian race” idea in the late eighteenth century and its subsequent career.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Several medieval and early modern European notions about differences and boundaries between peoples established ideational building blocks for modern race thinking. They sustained ethnic divisions within and beyond Europe that later shaped the shifting boundaries of modern racial categories, including nineteenth-century theories about the “races of Europe” that ruptured the preeminence of “Caucasian race” idea. One significant thread of this history was the development of the idea and actuality of “Europe.” After the collapse of the Roman Empire, the region now known as Europe gradually coalesced from great migrations of various peoples who criss-crossed the greater Eurasian landmass during the first millennium, including Angles, Saxons, Celts, Franks, Gauls, and Slavs.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("These peoples did not constitute human “races,” and they did not understand themselves as such. Moreover, before the establishment of relatively "), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }), _vm._v("well defined national “homelands” in the later medieval period, they were constantly in motion in the western (now European) part of Eurasia. Norman Davies comments, “It was difficult for tribes to move without coming into contact, and potential conflict, with their predecessors on the trail. . . . There is absolutely no reason to suppose that Celts, Germans, Slavs, and others did not overlap, and sometimes intermingle. The idea of exclusive national homelands is a modern fantasy.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en2"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2")])]), _vm._v(" This first great migration period in European history did not really end until “the arrival of Turkic peoples in Greece and the Balkans in the thirteenth through the sixteenth centuries.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en3"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en3",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3")])]), _vm._v(" This flux in Europe’s formative period informs its subsequent ethnic and racial history.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("A few constellations of medieval ideas and concepts were especially significant for the development of the “race” concept and the shifting boundaries of modern racial categories: the ideas of “Christendom” and, later, “Europe,” which marked a religiously, culturally, and geographically distinct region; the profound (and often race"), _c('em', [_vm._v("like")]), _vm._v(") alienness or otherness that various peoples in the Middle Ages sometimes ascribed to other peoples, especially those who did not share their religion; and the fact that European Christians adopted the Judaic account of creation and biblical chronology, along with an Old Testament view of the origins of various tribes of human beings. In addition, Cedric Robinson identifies three historical developments that joined this mix to generate modern racial thought:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("the Islamic (i.e., Arab, Persian, Turkish, and African) domination of the Mediterranean civilization and the consequent retarding of European social and cultural life: the Dark Ages; the incorporation of African, Asian, and peoples of the New World into the world system emerging from late feudalism and merchant capitalism [i.e., between the fifteenth and seventeenth centuries CE; and] the dialectic of colonialism, plantocratic slavery, and resistance from the sixteenth century forward, and the formation of industrial labor and labor reserves."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en4"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en4",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("Some of these developments were largely internal to Europe, while the others were indicative of Europe’s competition with the Islamic world and its increasing global power and reach.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Medieval Europeans employed certain racelike ideas to comprehend their social order and differentiate between social groups, but these were "), _c('em', [_vm._v("ethnic")]), _vm._v(" rather than "), _c('em', [_vm._v("racial")]), _vm._v(" designations. Still, a few cases of religious and "), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }), _vm._v("ethnic conflict in early modern Europe were quite close to modern racism and paved the way for it: Christian anti-Jewish and anti-Muslim ideas and movements that peaked after the fifteenth-century Christian “reconquest” of Spain; associations of “black” Africans with servile status that emerged in the Middle Ages and were compounded when Portuguese sailors acquired West African slaves in the fifteenth century; English Protestant colonial domination of Irish Catholics, particularly between the fifteenth and seventeenth centuries; and conflicts between Franks and Gauls and Normans and Saxons in early modern England and France.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("By the latter half of the seventeenth century, race-thinking proper emerged. English settlers in colonial America developed a racial understanding of and justification for the perpetual servitude to which they subjected “black” Africans, and they began to define themselves and other Europeans as “white” people. In relation to the new knowledge of the world and its peoples and the new global politics of the age, European scientists built upon the Scientific Revolution of the sixteenth and seventeenth centuries to develop the new science of “natural history.” They sought to explain the world’s natural order and the place of human beings within this order. One notable product of natural history was the first recognizably modern racial classification, in 1684, by the French travel writer François Bernier.")]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('strong', [_c('em', [_vm._v("Europe and Its “Others”")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Compared to the steady migrations that characterized Latin Christendom and nascent “Europe” in Late Antiquity and the early Middle Ages, the feudal social order that developed in this region during the Middle Ages (ca. 750–1453 CE) was relatively static and marked by a deeply sedimented status hierarchy. “The serf, the slave, the peasant, the artisan, the lord, the king—all were allotted their place in the world by divine sanction. Not just human order but natural order was preordained.” All living things were seen as joined in a “Great Chain of Being [that] linked the cosmos from the most miserable mollusc to the Supreme Being.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en5"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en5",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5")])]), _vm._v(" There were also significant distinctions between communities. Ethnocentrism was common between religious and ethnic groups—tribes ("), _c('em', [_vm._v("gens")]), _vm._v("), peoples ("), _c('em', [_vm._v("ethne")]), _vm._v("), and nations ("), _c('em', [_vm._v("natios")]), _vm._v(");"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en6"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en6",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6")])]), _vm._v(" Christians, Jews, Muslims, and pagans; the “civilized” and the “barbarian.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en7"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en7",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("7")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _vm._v("Yet these medieval divisions were different from later racialized divisions."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en8"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en8",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("8")])]), _vm._v(" This difference, which is important for understanding race and racism, has been obscured in some recent writing about medieval Europe. Robert Bartlett, for instance, examines what he calls the “race relations” of Latin Christendom between 900 and 1250 CE."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en9"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("9")])]), _vm._v(" He says that “while the language of race [in Latin Christendom]—"), _c('em', [_vm._v("gens, natio,")]), _vm._v(" ‘blood,’ ‘stock,’ etc.—is biological, its medieval reality was almost entirely cultural. . . . When we study race relations in medieval Europe we are analysing the contact between various linguistic and cultural groups, not between breeding stocks.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en10"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("10")])]), _vm._v(" Bartlett cites the canonist Regino of Prüm’s four criteria for classifying “ethnic variation” as a classic medieval formulation of medieval “race relations.” According to Regino (d. 915), “The various nations differ in descent, customs, language, and law.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en11"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("11")])]), _vm._v(" Bartlett comments that only Regino’s first criterion, “descent,” is central to modern racism, and he goes on to note that “Regino’s other criteria—customs, language, and law—emerge as the primary badges of ethnicity.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en12"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("12")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Bartlett is correct to speak of ethnicity in this context, but he confuses matters by describing medieval Europeans’ notions of ethnic difference in terms of race relations."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en13"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("13")])]), _vm._v(" Medieval Europeans, along with the peoples in other parts of the premodern world, lacked any concept comparable to the modern “race” concept. They emphasized cultural criteria of difference and lacked any clearly developed notion of “fixed natures” of different descent groups."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en14"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("14")])]), _vm._v(" For instance, medieval Christian Europeans used "), _c('em', [_vm._v("Arab, Saracen,")]), _vm._v(" and later "), _c('em', [_vm._v("Turk")]), _vm._v(" as terms for “Muslim”; Muslims of the Ottoman Empire referred to Europeans as “Franks”; and followers of each religion regarded members of the other faith as “infidels.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en15"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en15",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("15")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The historical development of the idea and territory of Europe was itself one of the most important antecedents of the modern “race” concept. Europe has sometimes been considered a distinct continent, but this is a misnomer (see figure 1). The region we now know as Europe is actually a peninsula of the Eurasian landmass."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en16"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en16",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("16")])]), _vm._v(" Europe, as Bartlett says, “is both a region and an idea.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en17"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en17",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("17")])]), _vm._v(" The societies and cultures that inhabited the western peninsula of the Eurasian landmass were always diverse. But by the later Middle Ages there was enough commonality among the areas that we now call western and central Europe to constitute a distinct region: “When compared with other culture areas of the globe, such as the Middle East, the Indian subcontinent or China, western or central Europe exhibited (and exhibits) distinctive characteristics. In particular, Latin Europe (that is, the part of Europe that was originally Roman Catholic rather than Greek orthodox or non-Christian) formed a zone where strong shared features were as important as geographical or cultural contrasts.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en18"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en18",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("18")])])]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_26"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f0026-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Figure 1: Queen Europe (Regina Europe), from Sebastian Müntzer’s "), _c('em', [_vm._v("Cosmography")]), _vm._v(" (1588). Reprinted from "), _c('em', [_vm._v("Early Modern Europe: An Oxford History")]), _vm._v(" (Oxford: Oxford University Press, 1999), ed. Euan Cameron, p. 20 (British Library, "), _c('span', {
    staticClass: "small"
  }, [_vm._v("MAPS")]), _vm._v(" C21 C13).")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_27"
    }
  }), _vm._v("Europe was becoming a kind of “imagined community” in political scientist Benedict Anderson’s sense: a loose but geographically and culturally bounded society “conceived as a deep, horizontal comradeship,” despite having no obvious or natural boundary to its eastern flank and a somewhat indeterminate southern frontier."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en19"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en19",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("19")])]), _vm._v(" Because of its geographical indeterminacy, the idea and boundaries of Europe have been conceived as much in opposition to other cultural and political zones as in relation to what “Europeans” have had in common. For instance, the status of Russia as properly “European” has long been contested, both within the core of Europe and among Russian intellectuals."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en20"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en20",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("20")])]), _vm._v(" The “modernizing” regimes of Peter I (Peter the Great, r. 1682–1725) and Catherine II (Empress Catherine, r. 1762–96) “Europeanized” Russia (Catherine declared Russia “a European state” in 1767); yet they did not resolve the issue. Due to this ambiguity, Europe’s “official” eastern frontier constantly shifted. “It advanced steadily from the Don, where it had been fixed for a thousand years, to the banks of the Volga at the end of the fifteenth century, to the Ob in the sixteenth, to the Ural river and the Ural mountains in the nineteenth, and finally to the river Emba and the Kerch straits in the twentieth.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en21"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en21",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("21")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("To a large extent, the consolidation of Europe was spurred by the rise and expansion of Islam in the seventh and eighth centuries."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en22"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en22",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("22")])]), _vm._v(" The expansion of Islamic societies isolated “Christendom” from the rest of the world and defined Europe’s boundaries. Muslims also established a major presence at the edges of Europe: first in Iberia (in the seventh century) and later in the Balkans and the Black Sea region (in the fourteenth and fifteenth centuries) and Hungary (capturing Belgrade in 1521), being repelled at Vienna in 1529 and again, decisively, in 1683."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en23"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en23",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("23")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Ongoing contact and conflict between Christians and Muslims had a direct impact on the place of the Caucasus region vis-à-vis Europe and Asia. Several centuries of enslavement of Christians by Muslims and of Muslims by Christians, for instance, continued into the sixteenth and seventeenth centuries. Meanwhile, in the western part of Europe, slavery had largely been done away with between the thirteenth and fifteenth centuries. This achievement followed the consolidation of a sense of “Christian brotherhood” in opposition to perceived Muslim and Jewish “outsiders.” This new collective identity was exemplified by the Crusades between the eleventh and thirteenth centuries. This was the context in which "), _c('a', {
    attrs: {
      "id": "page_28"
    }
  }), _vm._v("thousands of Caucasians, including Georgians, Armenians, Circassians, and Mingrelians, were bought and sold as slaves, along with the Moors, by Venetian and Genoese merchants. These groups “were classified as infidels even if they were eventually baptized.” Italian merchants “sold such ‘Slavs’ . . ., along with Greeks and Turks, in Muslim markets as well as in Christian Crete, Cyprus, Sicily, and such Spanish regions as early-fifteenth-century Valencia.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en24"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en24",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("24")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This source of slaves was mostly abandoned after the Ottoman Turks’ conquest of Constantinople in 1453, which redirected slave traders to sub-Saharan Africa."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en25"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en25",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("25")])]), _vm._v(" At that time the Portuguese sold increasing numbers of black African slaves. Prior to this Atlantic slave trade, slavery was already a prominent part of the economic and social history of European and Islamic societies in the Middle Ages. Yet "), _c('em', [_vm._v("racialized")]), _vm._v(" slavery emerged only with the development of Atlantic slave trade in the sixteenth and seventeenth centuries (see below).")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The Caucasus region sits squarely in greater Eurasia, on the isthmus between the Black and Caspian Seas where Europe and Asia meet; but even though it is partly Christian (primarily in Armenia and Georgia), it has never really been considered part of Europe. Christianity was adopted by leaders of Armenia and Georgia in 314 and 330 CE, respectively; Persian (Iranian) influences arrived in the fifth and sixth centuries, and Islam followed in the seventh century."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en26"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en26",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("26")])]), _vm._v(" After the Ottoman conquest of Constantinople in 1453, the Ottomans cut Georgia off from western Christendom, and between the sixteenth and eighteenth centuries the region become of a site of Turkish and Persian domination and competition. Russians eventually occupied the northern Caucasus (though without initially controlling it) in the eighteenth century, and Russia annexed Georgia in the nineteenth century."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en27"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en27",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("27")])]), _vm._v(" In the sixteenth century, English travelers to Russia, Persia, and central Asia tended to describe the people of this region as “brutish” and as “infidels.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en28"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en28",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("28")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("To the south of Europe, northern Africa was somewhat known to Europeans. Although never considered a part of Europe, North Africa “was a frontier region where Berber states and Ottoman client rulers posed a constant threat to the settled places of Christendom until the extinction of Turkish hegemony in the Mediterranean in the late seventeenth century.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en29"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en29",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("29")])]), _vm._v(" Africa south of the Atlas Mountains, however, remained largely unknown to Europeans until the nineteenth century, despite Portuguese exploration and settlement on parts its western shores that began in the fifteenth century."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en30"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en30",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("30")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_29"
    }
  }), _vm._v("European elites also used a twofold division of the world’s peoples between “civilized” and “uncivilized” (sometimes “barbarian”) peoples to gauge their place in the world."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en31"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en31",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("31")])]), _vm._v(" By the sixteenth and seventeenth centuries, European elites typically judged Europe to be the world’s most “advanced” society, but they also tended to include parts of Africa and Asia in the “civilized” world."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en32"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en32",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("32")])]), _vm._v(" Meanwhile, before the European feudal monarchies and merchant capitalists turned their energies to colonizing the Americas in the fifteenth century, their main “focus of external interest (and concern) was the Middle East, North Africa and India, collectively known as the Orient.” Through their descriptions of the peoples of these areas, European elites created not only “a discourse of an imagined Other at the edge of European civilisation, but . . . [also] a discourse of a real Other represented as a result of conflicting material and political interests with a population which came to mark the boundary of Europe, not only spatially but also in consciousness.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en33"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en33",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("33")])]), _vm._v(" This combination of perceived likeness and discrepancy between Europe, on the one hand, and “the Orient,” on the other hand, probably influenced the contours of the later “Caucasian race” notion: a Europe-centered notion, invented by Europeans to designate what they took to be the greatest and most beautiful “race,” it usually has been conceived so as to encompass peoples from parts of the Middle East, North Africa, and India as well as Europeans.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Another notable factor in the development of European racial thought was the legacy in Christian Europe of the Old Testament Judaic chronology (i.e., the idea of a five-thousand-year history of the earth since its creation), along with the biblical view of the common origins of various tribes of human beings through Adam and Eve, and then through Noah and his sons after the great flood described in Genesis. The biblical account is unclear about the geographic dispersion of Noah’s sons, Ham, Shem, and Japhet, and medieval interpreters associated them with the regions of Europe, Asia, and Africa in varying ways."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en34"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en34",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("34")])]), _vm._v(" Gradually, the dominant view of the narrative that took hold between the fourth and fourteenth centuries regarded Japhet as the progenitor of the peoples of Europe; Shem, those of Asia; and Ham, those of Africa. “Jerome’s rendering of Genesis 9:27, God ‘shall enlarge Japhet and he shall dwell in the tents of Shem,’ was taken to mean that Japhet would produce more offspring than his brothers and that, one day, his progeny would come to conquer Asia just as they would inherit from the Hebrews the mantle of the true religion.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en35"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en35",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("35")])]), _vm._v(" By the early modern period, versions of the biblical view of human dispersion through Shem, Ham, and Japhet after the flood were "), _c('a', {
    attrs: {
      "id": "page_30"
    }
  }), _vm._v("circulated by writers like Dutch jurist and diplomat Hugo Grotius (1583–1645) and Giambattista Vico (1668–1744), who proposed a “new science” of the human origin that melded “sacred history” with elements of a modern historical consciousness."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en36"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en36",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("36")])]), _vm._v(" In addition, the Bible’s account of Noah’s curse on Ham, which condemned Ham’s son Canaan and his descendants to servitude for Ham’s transgression against Noah, was eventually elaborated into an explanation and justification for the “blackness” and enslavement of sub-Saharan Africans (see below)."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en37"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en37",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("37")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Another set of ethno-cultural divisions in medieval Europe influenced the later contours and career of the “Caucasian race” idea. Discourses of otherness became prevalent that aimed at peoples who are now counted as Europeans. While these intra-European divisions were not originally conceived in racial terms, some of them reemerged as racial distinctions in the nineteenth century. Bartlett explains that")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("images of exclusion and otherness available to those who formed and expressed opinions in twelfth-century western Europe included not only the dichotomy Christian/non-Christian, but also that of civilized/barbarian, and the two polarities were often mutually reinforcing. The Welsh were “rude and untamed” and hence “nominally profess Christ but deny him in their life and customs.” The Ruthenians, who “confess Christ only in name, but deny him in their deeds,” were associated with other “primitive Slavs” and “wild peoples” of “uncivilized barbarism.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en38"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en38",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("38")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Adherence to the Latin Christian liturgy and obedience to the pope were necessary but not sufficient conditions for the full inclusion of a people within the emerging European society of the late Middle Ages. As the peoples of Latin-“Frankish” (i.e., Germanic) Europe “intruded upon societies around and unlike their own, they found both non-Christians (in eastern and Mediterranean lands) and local variants of Christianity (notably in Celtic countries). Their response was to equate the two, if the Christian societies did not have the social and legal characteristics with which they were familiar.” That is, they tended to see both kinds of societies as alien or “uncivilized.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en39"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en39",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("39")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Medieval writers such as Geoffrey of Monmouth (in his 1135 "), _c('em', [_vm._v("History of the Kings of Britain")]), _vm._v(") and Gerald of Wales (1146–1223) elaborated notions of ethnic otherness that “became rudiments of later racial discourse.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en40"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en40",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("40")])]), _vm._v(" Geoffrey’s influential book interpreted antagonisms between English and Norman peoples as an “ancient clash of Britons and Germanic invaders”; "), _c('a', {
    attrs: {
      "id": "page_31"
    }
  }), _vm._v("Gerald, in books on Wales and Ireland, related biological, political, and territorial categories of kin, tribe, people, and nation to climate, topography, environment, social organization, customs, language, military practice, and national character."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en41"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en41",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("41")])])]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('strong', [_c('em', [_vm._v("The Road to Racialism")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("A few ethnic notions that emerged in and around Europe in the Middle Ages and early modern period were especially portentous for the rise of the “race” concept in the seventeenth century and theories about “races of Europe” in the nineteenth. Foremost among these were Latin Christian European elites’ ideas about the otherness of Jews, Muslims, Irish, and Slavic peoples within Europe and of black Africans and “Indians” of the Americas. Also significant were ethnic and social status distinctions between Franks and Gauls in France and between Normans and Saxons in England.")]), _vm._v(" "), _c('p', {
    staticClass: "indent1"
  }, [_vm._v("Spain, circa 1492: Jews, Muslims, and “Indians”")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("There was a significant Jewish population in the Iberian peninsula in the fourteenth century."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en42"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en42",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("42")])]), _vm._v(" Anti-Jewish riots in Spain in 1391 marked a period of heightened conflict. Jews were given a choice of conversion or death, and many Jews converted to Christianity. In 1492, at the time of the completion of the Christian reconquest of Spain from Muslims, Jews as such were expelled from Spain. More Jews chose baptism and converted to Christianity to avoid death or expatriation."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en43"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en43",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("43")])]), _vm._v(" (Forced conversions in Portugal followed in 1497.) These conversions created a group of hundreds of thousands of formerly Jewish “new Christians,” or "), _c('em', [_vm._v("conversos,")]), _vm._v(" who did not readily assimilate into Spain’s Christian society. Many retained Jewish customs (often secretly) despite their outward change in religion."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en44"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en44",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("44")])]), _vm._v(" Muslims who remained in Spain after the reconquest likewise were forced to convert. These formerly Muslim new Christians, the "), _c('em', [_vm._v("Moriscos,")]), _vm._v(" were largely peasants and artisans in Spain. They lived in separate communities and resisted even the appearance of cultural assimilation and loss of their “Moorish” culture."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en45"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en45",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("45")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Despite the Christian belief in the regenerative character of baptism, the treatment of the "), _c('em', [_vm._v("conversos")]), _vm._v(" and "), _c('em', [_vm._v("Moriscos")]), _vm._v(" in Spanish law and theology after the reconquest prefigured later racism. Spanish theologians developed "), _c('a', {
    attrs: {
      "id": "page_32"
    }
  }), _vm._v("“a doctrine according to which the false beliefs of both the Moors and Jews had soiled their blood, and this stain or ‘nota’ had been transmitted by heredity to their furthest descendants, who were set apart in the almost untouchable caste of New Christians.” Laws were passed to maintain “purity of blood” among people of “genuine” Christian descent."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en46"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en46",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("46")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In Europe more generally, Jews were expelled from a number of countries in the later Middle Ages, and European Christians developed anti-Jewish myths that established a basis for later racialized (and "), _c('em', [_vm._v("racist")]), _vm._v(") anti-Semitism."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en47"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en47",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("47")])]), _vm._v(" Due to stiffened Christian prohibitions on “usury” combined with the relatively small amount of capital needed for moneylending, moneylending became one of the more widely practiced activities among Jews. As Jews increasingly worked in the fields of finance and moneylending, “these occupations shaped the stereotypical image of the Jew living in Christendom.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en48"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en48",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("48")])]), _vm._v(" Christians also regarded Jews as aliens in Europe in other ways. The strangeness of Jewish religious rites when measured against Christian practices was construed as a sign of a deviant stubbornness that betrayed the devil’s influence. In a Lenten sermon in Trent, in 1475, the Italian friar Bernardino of Feltre said that “he had sometimes heard it said that Jews drink the blood of Christians at Eastertide,” and that year the Jews of Trent were accused of ritual murder."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en49"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en49",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("49")])]), _vm._v(" Similar incidents occurred in Italy and Spain. Myths also circulated about Jews cooking their Passover bread ("), _c('em', [_vm._v("matzot")]), _vm._v(") with Christian blood and “drinking Christian blood to eliminate the smell associated with their diabolical nature.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en50"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en50",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("50")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The reconquest and the expulsion of Jews and Muslims from Spain in 1492 coincided with the voyage of the Genoese sailor Cristoforo Colombo (Christopher Columbus) to “America” while in search of a new route to Asia."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en51"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en51",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("51")])]), _vm._v(" The voyages to the Americas by Colombo, Amerigo Vespucci, and the conquistadors Hernán Cortés and Francisco Pizarro became another link between medieval notions of otherness and modern racial thought. The cruelty inflicted on Native Americans by the European conquerors and settlers provoked a great debate among European clergy about the rights of conquerors and the humanity and dignity of the Native Americans. A key episode in this debate was the exchange in Spain, during 1550–51, between Bartolomé de Las Casas, the Dominican bishop of Chiapas, and Juan Ginés de Sepúlveda, a humanist scholar and translator of Aristotle. The gist of the debate concerned whether or not indigenous Americans possessed human reason. Sepúlveda regarded Native Americans as subhuman beings who lacked any elements of civil life and virtue, and he "), _c('a', {
    attrs: {
      "id": "page_33"
    }
  }), _vm._v("applied Aristotle’s notion of “natural slavery” to them. He claimed that hierarchy rather than equality was that natural order of human society."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en52"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en52",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("52")])]), _vm._v(" Las Casas countered from the perspective of Christian universalism: “The natural laws and rules and rights of men are common to all nations, Christian and gentile, and whatever their sect, law, state, color and condition, without any difference.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en53"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en53",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("53")])]), _vm._v(" He maintained that as people equipped with reason the indigenous Americans, though not Christian, could "), _c('em', [_vm._v("become")]), _vm._v(" Christian."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en54"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en54",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("54")])]), _vm._v(" Still, while Las Casas opposed indiscriminate exploitation of the Americans, he affirmed the superiority of Christian European “civilization” and thus legitimated Spanish imperial expansion to the Americas."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en55"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en55",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("55")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent1"
  }, [_vm._v("Slavic Peoples")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In the case of the Slavs, as with the other peoples that I have been discussing, we should avoid any suggestion of some “pure,” primordial Slavic people."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en56"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en56",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("56")])]), _vm._v(" Yet, by the Middle Ages, Slavic peoples had coalesced in a recognizable way, with a distinct language and culture, in areas that would become eastern Europe. The marginal place of Slavs (including later Czechs, Slovaks, Poles, Ukrainians, Russians, Serbs, and Croats) in the medieval European world is indicated in the following comment, which the English historian Edward Gibbon adapted from the sixth-century Byzantine historian Procopius and from the Emperor Mauritius:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("The Sclavonians used one common language (it was harsh and irregular) and were known by the resemblance of their form which deviated from the swarthy Tartar and approached, without attaining, the lofty stature and fair complexion of the German. Four thousand six hundred villages were scattered over the provinces of Russia and Poland, and their huts were hastily built of rough timber."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en57"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en57",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("57")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("This sense of Slavic distinctiveness was reinforced—if not generated—by the somewhat unique association of Slavic peoples with slavery. While slavery was a declining practice "), _c('em', [_vm._v("within")]), _vm._v(" most of western Europe, it persisted in Slavic regions. Russia and the Ukraine had the most well developed system of slavery in Europe from the twelfth through the seventeenth centuries, and the Mongol conquest of 1237–1240 resulted in the enslavement of about 10 percent of the eastern Slavs. This made the Slavic areas into one of the world’s two great sources of slaves, along with Africa."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en58"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en58",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("58")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }), _vm._v("One consequence of this strong link between Slavic peoples and slavery is that the word "), _c('em', [_vm._v("slave")]), _vm._v(" in many European languages comes from the Latin word "), _c('em', [_vm._v("sclavus,")]), _vm._v(" which means a person of Slavic origin."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en59"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en59",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("59")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In Russia, slavery continued into the seventeenth century. It was replaced by serfdom in the seventeenth and early eighteenth centuries. In Poland, there were privately owned slaves in the Middle Ages that eventually blended into serfs in the late Middle Ages. In the Balkans, slavery had been largely eclipsed by serfdom after 1100 under Byzantine rule, which was Orthodox Christian and used Roman law; it was reintroduced in the fifteenth century in the parts of the Balkans that came under the control of the Ottoman Empire, which was Islamic."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en60"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en60",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("60")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The Ottomans’ slaves were typically outsiders. Between 1500 and 1700, Crimean Tatars captured and sold as slaves approximately 2.5 million Russians, Ukrainians, Poles, and Hungarians."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en61"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en61",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("61")])]), _vm._v(" Most of them went to the Ottoman Empire. Crimeans continued to enslave Poles into the seventeenth and eighteenth centuries, but Russia, under Catherine the Great, ended Crimean slave trading in 1783 when it conquered the Ottomans’ Black Sea territories and Crimea, before it extended its reach into Persia, the Caucasus, and central Asia. “After that, slaves in the Ottoman Empire were so-called ‘white slaves’ kidnapped from the Caucasus (Circassians and Georgians) or black slaves imported through Egypt from Africa.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en62"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en62",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("62")])]), _vm._v(" Marginalization of Slavic peoples in Europe was also manifest in other ways. For example, beginning in the early fourteenth century in German-dominated regions of eastern Europe, discriminatory urban legislation was enacted against people of Slavic “stock.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en63"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en63",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("63")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent1"
  }, [_vm._v("“Black” Africans")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Awareness of differences in the skin color and appearance among the world’s peoples can be found in some of the earliest human efforts to record history, geography, and travels. Yet the “color consciousness” that emerged among various peoples before the modern era—including ideas about “white” and “black” peoples in ancient and medieval China—was distinct from modern racialism and “race consciousness.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en64"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en64",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("64")])]), _vm._v(" Thus, the ancient Greek historian Herodotus, who wrote his "), _c('em', [_vm._v("Histories")]), _vm._v(" in the fifth century BCE based on his travels across the Persian Empire, noted differences in the skin color and appearance among different peoples. In the Middle Ages, Marco Polo (1254–1324), the Venetian traveler, and Ibn Batutah (1304–77), the Muslim geographer from Morocco, recorded the skin color "), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }), _vm._v("and appearance of the different peoples they encountered, whom they also described with cultural and religious notions such as “infidels.” These writers, however, lacked any concept comparable the modern “race” concept with which to interpret these differences."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en65"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en65",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("65")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The ancient Greeks were aware of “black-faced” peoples of Africa (“Ethiopians”), whom they distinguished from Egyptians, but had no sense of any natural inferiority of Africans. Diodorus, the late-first-century BCE Greek historian, regarded some Ethiopians as “primitive” and considered others as “the "), _c('em', [_vm._v("first of all men")]), _vm._v(" and as the originators of divine rituals most pleasing to the gods.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en66"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en66",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("66")])]), _vm._v(" On the whole, the view of black Africans among the ancient Greeks and Romans was positive. Frank Snowden explains, “Initial, favorable impressions were not altered, in spite of later accounts of wild tribes in the south and even after encounters with blacks had become more frequent. There was a clear cut respect among Mediterranean peoples for Ethiopians and their way of life.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en67"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en67",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("67")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("At the same time, the ancient Greeks and Romans as well as medieval Europeans distinguished between black and fair-skinned peoples in ways that conveyed deep symbolic meaning. Stereotypical formulations such as “Can the Ethiop change his skin?” were common in Greco-Roman and Judeo-Christian cultures. The usual implication was that “whiteness” was the positive norm and “blackness” signified a deficiency. By the fourteenth century in Europe, visual and literary images of “black” physiognomies had become familiar markers of territorial distance and cultural exoticism."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en68"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en68",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("68")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("By the late Middle Ages, the Old Testament story of the curse of Ham became a stock justification among Europeans for the enslavement of black Africans. According to the book of Genesis, after the flood Ham disrespected his father Noah by looking at his father’s naked body as Noah lay drunk. When Noah awoke, he cursed Ham’s son Canaan and his descendants for Ham’s transgression, declaring that they would be “servants unto servants.” Significantly, the narrative is vague about the geographical destinations of Noah’s sons and their descendants, and although it concerns servitude it says nothing about skin color."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en69"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en69",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("69")])]), _vm._v(" In medieval Europe—where serfs, unlike Jews, Muslims, and lepers, were a numerical majority—the story had been used to explain and justify the subordination of serfs as the descendants of Cain or Ham."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en70"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en70",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("70")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The curse of Ham’s son Canaan seems to have been first used to link slavery to “blackness” in the Islamic world during the Middle Ages."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en71"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en71",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("71")])]), _vm._v(" Medieval Arabs and Moors used both light-skinned and “black” slaves, but typically relegated “blacks” to the most menial and degrading work."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en72"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en72",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("72")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_36"
    }
  }), _vm._v("Muslim expansion into Asia and Africa from the eighth century produced a color consciousness developed in the Islamic world."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en73"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en73",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("73")])]), _vm._v(" Muslim thinkers such as the fourteenth-century historian Ibn Khaldun commonly expressed derogatory views of black Africans. Khaldun once wrote that black Africans “are closer to dumb animals than to rational beings.” (He held a similar view of Slavs.)"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en74"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en74",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("74")])]), _vm._v(" These notions were not sufficient to establish a special link between slavery and black Africans, however, because medieval Muslims never developed a distinctly racialized form of slavery. Muslims as well as Christians began to associate sub-Saharan Africans with lifetime servitude when black Africans became conspicuous in southern Iberia as the slaves of light- or tawny-skinned Moors. And this identification of “black skins with servile status” deepened as Europeans ceased to enslave other Europeans and Portuguese sailors acquired slaves during voyages to the Guinea coast in the fifteenth century (see “The ‘Race’ Concept” section, below)."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en75"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en75",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("75")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent1"
  }, [_vm._v("England and France: Gauls and Franks, Normans and Saxons")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("With the Gauls and Franks we return to divisions within Europe. “Francia” or “Frankland” (later France) grew up in the sixth century from Frankish kingdoms established in largely Gallo-Roman territories: Neustria, in the west, comprising primarily Gallo-Romans centered in Paris; Austrasia, east of the Rhine and predominantly Germanic; and Burgundy, which included the old Burgundian kingdom by the Rhône and much of Gaul."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en76"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en76",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("76")])]), _vm._v(" (Celtic peoples had settled in the west, forming Brittany.)"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en77"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en77",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("77")])]), _vm._v(" By the middle of the sixth century in Neustria and Austrasia, most of the elite population, clergy, nobility, and government officials, considered themselves Franks. These Frankish rulers incorporated Burgundy into the larger Frankish kingdom but respected Burgundian social and legal tradition; a sense of Burgundian regional identity was guarded by the area’s aristocracy."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en78"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en78",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("78")])]), _vm._v(" As of the early seventh century, “regional populations were divided by social strata, not by language, custom, or law; and all of society, . . . with the exception of the Jewish minority, were united in a single faith.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en79"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en79",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("79")])]), _vm._v(" By the end of the first millennium the distinction between Gallo-Romans and Franks had disappeared, along with an earlier distinction between Romans and barbarians. This development, however, did not prevent a distinction between Franks and Gallo-Romans (or Gauls) from being recreated in the sixteenth century when the problem of national origins surfaced."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en80"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en80",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("80")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_37"
    }
  }), _vm._v("Etymologically, the term "), _c('em', [_vm._v("Frank,")]), _vm._v(" referring to a person of Germanic origin, is linked in western languages to the concepts “freedom, integrity and power” as well as to Francia, Frankland, and France. To be called a Frank during the Middle Ages connoted not only a Germanic person but also a “free” person—a status that stood in sharp contrast to that of a serf ("), _c('em', [_vm._v("servus")]), _vm._v(") or slave ("), _c('em', [_vm._v("Slav")]), _vm._v("). Léon Poliakov remarks that a key word in French political history thus “slyly hint[s] at the superiority of the German stock over the Latins and over the Slavs.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en81"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en81",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("81")])]), _vm._v(" The Frank/Gaul distinction that was reintroduced in the sixteenth century “made an important contribution to the idea of race.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en82"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en82",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("82")])]), _vm._v(" It also exemplified some aspects of the interrelationship between race and class, particularly the way class divisions have sometimes been racialized."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en83"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en83",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("83")])]), _vm._v(" In fact, as I will explain, the Frank/Gaul distinction eventually was conceived in an explicitly racialized manner by prominent French writers in the decades after the French Revolution of 1789.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On the Gallic or Gaulish side, the writer François de Belleforest (d. 1583) referred to “our ancestors the Gauls.” He suggested that “divine providence” had used the reign of Hugh Capet (r. 987–96) “to restore to the native Gauls authority over their country,” which had been usurped by the foreign Franks."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en84"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en84",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("84")])]), _vm._v(" For two centuries after Belleforest, the Gauls had few other advocates. The prevailing view asserted a Frankish-aristocratic preeminence in the making of France. During the reign of Louis XIV (r. 1643–1715) the writer Loyseau declared, “The victorious Franks were of noble birth; the vanquished Gauls were of common stock. The conquering Franks claimed for themselves the possession of arms, the administration of public offices, the enjoyment of their fiefs.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en85"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en85",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("85")])]), _vm._v(" This theme was reiterated in class-inflected, quasi-racialist terms in the early eighteenth century by champions of the nobility such as Comte Henri de Boulainvilliers, who “claimed a different descent from that of the commonality who were of Gallo-Roman ancestry.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en86"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en86",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("86")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In the era of the French Revolution, writers such as Abbé Sièyes challenged the myth of Germanic-aristocratic Franks with a bourgeois-revolutionary myth about the liberation of the enslaved Gauls. Sièyes, advocate of the rising bourgeoisie, reversed the roles of Franks and Gauls in his 1789 pamphlet "), _c('em', [_vm._v("What Is the Third Estate?")]), _vm._v(" He called for the Third Estate, as representatives of a new France descended from Gauls and Romans, to liberate the nation from its Frankish nobility: “Why should [the Third Estate] not relegate to the forests of Franconia all those families which persist in the foolhardy pretense of being descended from the race of conquerors . . .? The Nation, thus purged, would . . . be able to console itself "), _c('a', {
    attrs: {
      "id": "page_38"
    }
  }), _vm._v("by the thought that it was constituted of the descendants of the Gauls and the Romans only.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en87"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en87",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("87")])]), _vm._v(" Other new proponents of a “Gallic” France, such as La Tour d’Auvergne, wanted to restore the “illustrious race” of Gauls."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en88"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en88",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("88")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Later, pro-aristocratic successors to Boulainvilliers also resorted to racial appeals—now to Germanic-Frankish superiority—and prominent French historians of early-nineteenth-century France embraced racialized ideas about Franks and Gauls."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en89"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en89",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("89")])]), _vm._v(" According to historian Augustin Thierry, for instance (writing in 1820), “Recent studies in physiology . . . show that the physical and moral constitution of nations depends far more on their descent from certain primitive ancestors than on the influence of climate.” France, he adds, has descended from “two enemy camps,” and “whatever may have been the mixture of the two primitive races, their spirit of unending contradiction has survived to this day in the two parts of the combined population.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en90"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en90",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("90")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The case of England was similar to that of France. The British Isles were colonized during the first millennium by Iberians, Celts, Romans, Germans, and Scandinavians, capped off by the Norman conquest of 1066. While the terms "), _c('em', [_vm._v("British")]), _vm._v(" and "), _c('em', [_vm._v("English")]), _vm._v(" have become near synonyms, England’s origin myths recall the presence of Celts, Britons, Germans, and Anglo-Saxons (descended from Angles and Germanic Saxons) and also appeal to Hebrew narratives."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en91"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en91",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("91")])]), _vm._v(" The most salient myth concerns the conquest of the Norman (French, Frankish, Latin) elite over an established Anglo-Saxon, English-speaking people."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en92"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en92",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("92")])]), _vm._v(" In the sixteenth century a Saxonist and anti-French campaign against the “Norman yoke” emerged. Saxonist views became prominent during the seventeenth-century English revolutions, especially when the poorer classes voiced their grievances through popular movements such as the Levelers and the Diggers. These protests foreshadowed the class divide of the French Revolution. The Levelers and Diggers appealed to scriptures and to an idealized, egalitarian Anglo-Saxon past. King James I, in contrast, regarded the English nobility as mostly Norman so that “at the root of the class confrontation was a cultural confrontation which was perceived as a conflict between different bloods.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en93"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en93",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("93")])]), _vm._v(" After the Glorious Revolution of 1688, which established a constitutional monarchy and a restricted form of popular sovereignty, these ideas were expanded into a wider tradition of English and Anglophile paeans to the “love of civil liberty” and constitutional government among the Saxons. This tradition of thought reemerged in the 1840s in a racial theory of the Teutonic (Germanic–northern European) roots of English constitutional government."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en94"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en94",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("94")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent1"
  }, [_c('a', {
    attrs: {
      "id": "page_39"
    }
  }), _vm._v("The Irish")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The Irish experience is another thread in Europe’s emerging racial discourse. Ireland became part of European Christendom in 461 CE, not long after Gaul (France) in 397, and before England between 597 and 664."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en95"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en95",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("95")])]), _vm._v(" Even so, the Irish were subjected to processes of conquest, colonization, and cultural transformation similar to those used eastern Europe and Spain, two other peripheries of Frankish-Latin Europe."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en96"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en96",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("96")])]), _vm._v(" By the twelfth century the Irish were seen by elites of England, France, and Italy as alien to Latin Christendom. Bartlett explains, “Although the Irish were of ancient Christian faith and shared the creed of Frankish Europe, they exhibited pronounced differences in culture and social organization.” Critics regarded Irish social structures and customs as “barbaric” and “beastlike” and held that even though the Irish were Christian, they could be treated as though they were not."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en97"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en97",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("97")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("During the late Middle Ages, England made this view of Irish otherness an effective tool in their colonization of Ireland. At the request of England’s King Henry II (1154–89), Pope Adrian IV authorized England to rule over the Irish in order to expand the church’s boundaries and “to proclaim the truths of the Christian religion to a rude and ignorant people.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en98"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en98",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("98")])]), _vm._v(" The guiding assumption was that the Irish were fellow human beings but lived a less than fully human Christian life."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en99"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en99",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("99")])]), _vm._v(" As England’s noble families began to reside in Ireland, the English eventually developed policies such as the Statutes of Kilkenny (1366) to differentiate the English and the Irish of Ireland. The statutes acknowledged a “mixed nation” of English families that had intermarried with leading Irish families. To contain this “degeneracy” the statutes banned marriage, “concubinage[,] or amour” between the English and Irish and called for a strict separation between them in language and customs. The English at this time did not regard the Irish as biologically different but rather as “uncivilized.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en100"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en100",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("100")])]), _vm._v(" Moreover, until the sixteenth century feudal Ireland remained largely independent from England economically.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("England launched a more aggressive colonization under the Tudor Dynasty of Henry VII, Henry VIII, and Elizabeth I. By 1541, Henry VIII had enjoined the Anglo-Irish Parliament to proclaim him the spiritual head of the Church of Ireland, obliged all government officials in Ireland to swear allegiance to the church, and established the king of England as the king of Ireland."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en101"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en101",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("101")])]), _vm._v(" This marked the beginning of the Protestant ascendancy in Ireland. Under Elizabeth (r. 1558–1603) and James I (r. 1603–25), English "), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }), _vm._v("policy became more heavy-handed. This involved the expropriation of Irish-owned land, direct exploitation of Irish labor, and forced resettlement of those whose land had been taken."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en102"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en102",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("102")])]), _vm._v(" English policy included attempts to establish plantations in Ireland (mostly unsuccessful). The basic model was for English tenants to supplant Irish tenants and for native Irish to be made the primary laborers, with no rights to own land."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en103"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en103",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("103")])]), _vm._v(" With the Protestant Ascendancy and the enactment of the Penal Laws, in the sixteenth and seventeenth centuries, England devised a colonial regime that prefigured expressly racist forms of colonial rule that England adopted elsewhere in its empire. The Penal Laws effectively excluded Catholics, who were three-quarters of the Irish population, from all important positions in their country, ownership of property, and education."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en104"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en104",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("104")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Still, English colonial rule in Ireland from the mid–seventeenth to the late eighteenth century was something distinct from racial oppression."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en105"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en105",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("105")])]), _vm._v(" While English domination of the Irish was as harsh as many other cases of colonial subjugation, it was based on claims about cultural and religious differences rather than on race."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en106"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en106",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("106")])]), _vm._v(" The English occasionally used the term "), _c('em', [_vm._v("race")]), _vm._v(" in loose ways to refer to the “wild Irish,” but they lacked a systematic discourse of racial difference."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en107"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en107",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("107")])]), _vm._v(" For instance, English Protestants accepted the possibility of Irish Catholics "), _c('em', [_vm._v("becoming")]), _vm._v(" Protestant through conversion, something that is precluded by notions of "), _c('em', [_vm._v("racial")]), _vm._v(" difference."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en108"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en108",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("108")])]), _vm._v(" There is often a fine line between these two forms of oppression, however. Moreover, in nineteenth century, "), _c('em', [_vm._v("after")]), _vm._v(" the development of modern racialist thought, the Irish were often explicitly regarded in Europe and the United States as a distinct “Celtic race” by raciologists and in popular racist discourse (see "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch03#ch03",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapters 3")]), _vm._v(" and "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch04#ch04",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4")]), _vm._v(")."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en109"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en109",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("109")])])]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('strong', [_c('em', [_vm._v("The “Race” Concept")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("The modern concept of “race” has its roots in these earlier ethnic distinctions. Nonetheless, the invention of the “race” concept by Europeans in the seventeenth and eighteenth centuries constituted a historically novel "), _c('em', [_vm._v("naturalizing")]), _vm._v(" of differences between groups of people, a “new epistemology of human difference.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en110"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en110",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("110")])]), _vm._v(" The significance of this development becomes evident when we examine the origins of the word "), _c('em', [_vm._v("race")]), _vm._v(" and the “race” concept: when they first came into use, how they were first used, and to what effects."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en111"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en111",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("111")])]), _vm._v(" As Alan Ryan says, it is important to distinguish between the "), _c('em', [_vm._v("word")]), _vm._v(" and the "), _c('em', [_vm._v("concept")]), _vm._v(": “The existence-criteria of concepts may be obscure, but "), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }), _vm._v("they surely do not come into existence only when what they are concepts of comes into existence; nor do they cease to exist when it does.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en112"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en112",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("112")])]), _vm._v(" Concerning race, it is clear that this concept is "), _c('em', [_vm._v("not")]), _vm._v(" bound up with the “coming into existence” or ceasing to exist of actual "), _c('em', [_vm._v("human races.")]), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en113"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en113",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("113")])]), _vm._v(" This disjunction between the “race” concept and the coming into existence or ceasing to exist of human races is recognized by both racialists and social constructionists. The former maintain that biologically based human races actually exist, but that their “coming into existence” was an effect of ancient processes of “raciation,” understood as a fact of "), _c('em', [_vm._v("natural history")]), _vm._v(" that preceded by a long time the modern “race” concept."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en114"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en114",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("114")])]), _vm._v(" In contrast, social constructionists insist that there are no human races in the biological sense to come into or go out of existence, and that it is only "), _c('em', [_vm._v("people’s ideas and beliefs about race")]), _vm._v(" that have come into existence and can go out of existence—for example, the "), _c('em', [_vm._v("belief")]), _vm._v(" that human beings can be divided meaningfully into distinct races. Social constructionists also maintain that there are integral connections between the rise of the “race” concept and people’s various beliefs, theories, and social practices concerning race."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en115"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en115",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("115")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Regarding the distinction between words and concepts, James Farr observes that it often does not need to be explicitly “drawn because our language is so richly developed that most of our concepts—especially our political concepts—express themselves with matching words which name them explicitly and uniquely.” He adds, with reference to political concepts, “This is not always so, of course, for someone may possess a concept without (yet) possessing the matching word. . . . However, our ability to identify with any confidence our own much less others’ political concepts wholly depends upon the range of (other) words at our or their disposal.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en116"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en116",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("116")])]), _vm._v(" With the “race” concept, this means we can conclude with some assurance that people possess (or a society possesses) the "), _c('em', [_vm._v("concept")]), _vm._v(" only when they have developed a rich, expressive discourse that makes use of the concept with some consistency. Such a discourse of race would encompass a range of related words and concepts. This view leaves open the possibility that a group or society may possess the concept of race in a rudimentary form without (yet) being in possession of the matching word, "), _c('em', [_vm._v("race.")]), _vm._v(" But we need to tread carefully here. It is clear that the origins of the word "), _c('em', [_vm._v("race")]), _vm._v(" preceded by a couple of centuries or so the development of systematic ideas and theories of “race.” Meanwhile, the development of a systematic discourse of race between the seventeenth and nineteenth centuries had a profound, even revolutionary, effect on the meaning of the word."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en117"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en117",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("117")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_42"
    }
  }), _vm._v("The origins of the term "), _c('em', [_vm._v("race")]), _vm._v(" in European languages are murky. The word appeared around the late fourteenth century in Italian and Spanish. From there it migrated into other European languages—French and English in the sixteenth century; German (from French) in the eighteenth century."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en118"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en118",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("118")])]), _vm._v(" Eric Voegelin notes, “From its first appearance, the meanings of the word revolve around the fact of descent or origin.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en119"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en119",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("119")])]), _vm._v(" Through the sixteenth and seventeenth centuries the term had a wide range of connotations, some of which had only loose affinities to the modern “race” concept. Thus, John Foxe, in "), _c('em', [_vm._v("The Book of Martyrs")]), _vm._v(" (1570), spoke of “the outward race & stock of Abraham”; Milton, in "), _c('em', [_vm._v("Paradise Lost")]), _vm._v(" (1667), wrote, “High proof ye now have giv’n to be the Race Of Satan,” and he referred to “That Pigmean Race Beyond the Indian Mount”; Richard Hooker, in "), _c('em', [_vm._v("The Laws of the Ecclesiastical Polity")]), _vm._v(" (1594), said, “Such, as either we must acknowledge for our own forefathers or else disdain for the race of Christ.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en120"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en120",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("120")])]), _vm._v(" Another relevant early meaning was associated with animal husbandry and aristocratic lineages, denoting a “superior” or “noble” stock, family, or class “to which a person, animal, or plant belongs.” In this vein, Shakespeare, in "), _c('em', [_vm._v("A Winter’s Tale")]), _vm._v(" (1611), spoke of a “bud of a Nobler race.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en121"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en121",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("121")])]), _vm._v(" As late as 1790, Edmund Burke still used "), _c('em', [_vm._v("race")]), _vm._v(" to refer to an earlier generation of ancestors: “If the last generations of your country appeared without much lustre in your eyes, you might have . . . derived your claims from a more early race of ancestors.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en122"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en122",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("122")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In Europe during the Middle Ages, a concept of “race” in the modern sense was precluded by the prevailing biblical mode of thought. As long as human beings were understood as having a singular and homogeneous origin, they “could not be submitted to zoological divisions, or to the terms used to designate them.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en123"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en123",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("123")])]), _vm._v(" Beginning in the fifteenth century, several developments contributed to the modern European invention of the “race” concept: the development of racialized slavery out of more indiscriminate forms of medieval slavery and more limited forms of servitude in the Americas in the sixteenth and seventeenth centuries, along with the growth of the Atlantic slave trade; the gradual replacement of the feudal status order of medieval Europe with the relatively more fluid and ostensibly “meritocratic” class divisions of modern capitalist societies; emerging class conflicts over the availability of wage labor and the conditions of labor; vast new information about the peoples of the world in the age of European exploration and conquest between the fifteenth and seventeenth centuries; and the Scientific Revolution of the sixteenth and seventeenth centuries."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en124"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en124",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("124")])]), _vm._v(" Sixteenth-century Spanish writers began to use the term "), _c('em', [_vm._v("race")]), _vm._v(" with "), _c('a', {
    attrs: {
      "id": "page_43"
    }
  }), _vm._v("reference to the new populations that they encountered in their travels. Yet it was left to the English colonizers of North America, who developed a distinctly racialized form of chattel slavery in the late seventeenth century, first to employ the concept of race systematically to differentiate groups of people based on ideas about heritable and fixed physical characteristics."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en125"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en125",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("125")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("According to George Fredrickson, “The modern concept of races as basic human types classified by physical characteristics (primarily skin color) was not invented until the eighteenth century.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en126"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en126",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("126")])]), _vm._v(" Nevertheless, as I will explain shortly, the basic idea of race differences, whereby human beings were understood to be divisible into discrete physical types, possessed of supposedly unequal moral and intellectual capacities, was fairly well established by the late seventeenth century. By that time the Atlantic slave trade was flourishing, and racialized slavery (of West Africans) had become institutionalized through law and custom in England’s American colonies, particularly Virginia and Maryland. These developments were epitomized by the ideological, material, and legal lumping together of diverse West African peoples into the “race” of “Negroes.” “This ‘Negro,’” Robinson explains,")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("was a wholly distinct ideological construct from those images of Africans that had preceded it [e.g., as “African,” “Moor,” “Ethiop”]. It differed in function and ultimately in kind. Where previously the Blacks were a fearful phenomenon to Europeans because of their historical association with civilizations superior, dominant, and/or antagonistic to Western societies (the most recent being that of Islam), now the ideograph of Blacks came to signify a difference of [race or] species, an exploitable source of energy (labor power) both mindless to the organizational requirements of production and insensitive to the subhuman conditions of work."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en127"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en127",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("127")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("By the fifteenth century, Arab slave traders had long plundered African societies for slaves. Slavery existed in the Iberian peninsula prior to Spanish and Portuguese exploration of the Americas."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en128"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en128",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("128")])]), _vm._v(" These forms of slavery, however, were not "), _c('em', [_vm._v("racially")]), _vm._v(" defined."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en129"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en129",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("129")])]), _vm._v(" By contrast, the Atlantic trade in African slaves, initiated by Portugal, eventually produced the racialization of Africans as Negroes, along with the corresponding racialization of most Europeans (and some other peoples) as whites. This divide was arguably the pivot on which racial thought was further elaborated.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("After 1500, Portuguese slave traders began to supply Portuguese and Spanish settlements in the Americas with slave labor from enslaved West "), _c('a', {
    attrs: {
      "id": "page_44"
    }
  }), _vm._v("Africans. The terms "), _c('em', [_vm._v("negro")]), _vm._v(" and "), _c('em', [_vm._v("guineu")]), _vm._v(" appeared in Portugal in the fifteenth century. This reflected the fact that “as of that time, in Portugal, the most common kind of slave was . . . African.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en130"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en130",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("130")])]), _vm._v(" As I noted earlier, Spanish conquests over the native “Indians” of the Americas had forced them to debate how they should treat these populations. Although the Spanish often treated native Americans brutally, they (like the later English colonists) never made the enslavement of these indigenous peoples a major part of their colonial rule."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en131"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en131",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("131")])]), _vm._v(" Meanwhile, the Spanish and Portuguese turned the enslavement of Africans into a vast Atlantic commerce. By around 1550 the English had acquired from the Spanish “the notion that Negroes could be enslaved,” even though slavery (as opposed to temporary servitude) went against the grain of English law."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en132"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en132",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("132")])]), _vm._v(" At about the same time, the term "), _c('em', [_vm._v("negro")]), _vm._v(" was incorporated into the English language despite the fact that the term "), _c('em', [_vm._v("black,")]), _vm._v(" with the same basic meaning, already existed in English."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en133"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en133",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("133")])]), _vm._v(" By about 1650, the Spanish, Portuguese, English, and Dutch all used African slave labor almost exclusively on sugar plantations."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en134"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en134",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("134")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("One reason that European elites of the fifteenth through seventeenth centuries found slavery to be an acceptable institution in their colonies was that they were still influenced by “the heritage of the Middle Ages.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en135"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en135",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("135")])]), _vm._v("“Settlers from Europe,” Daniel Segal says, “brought with them hierarchical distinctions which meant that they were many, not one”—that is, they understood European peoples to be different ranks of persons."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en136"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en136",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("136")])]), _vm._v(" In the early seventeenth century, peasants constituted the vast majority of Europe’s population and servitude was widespread in England’s American colonies."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en137"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en137",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("137")])]), _vm._v(" The propertied classes in England began to promote sending their poorer countrymen (and fewer women) to the colonies as they grew concerned that a “mass of idle rogues and beggars grew and increasingly threatened the peace of England.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en138"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en138",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("138")])]), _vm._v(" The colonies became an outlet for an English population that grew rapidly between 1500 and 1650. Because most of the migrants could not afford passage to the American colonies, many of them came as indentured servants who were bound by contract to serve a master for a fixed number of years to repay their ocean transit."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en139"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en139",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("139")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In seventeenth-century England slavery had been ended, but “indentured” servitude, which did not entail a complete loss of freedom, was still practiced. Early in the century the English were somewhat reluctant to embrace the enslavement of Africans in their colonies. Thus, Richard Jobson said in 1623, in response to a suggestion to buy slaves, “We were a people, who did not deale in any such commodities, neither did we buy or sell "), _c('a', {
    attrs: {
      "id": "page_45"
    }
  }), _vm._v("one another, or any that had our owne shapes.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en140"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en140",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("140")])]), _vm._v(" Moreover, indentured servitude met most of the early needs of the English colonizers of America. Land was available to those who had capital, and labor was relatively scarce."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en141"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en141",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("141")])]), _vm._v(" Three primary forms of labor emerged: temporary servitude, based on complex contractual arrangements; “free” wage labor, involving the contractual exchange of labor for a wage; and chattel slavery, which began with captivity, involved no contractual agreements between slaves and masters, and was the last to develop."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en142"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en142",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("142")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This was the context in which a distinctly racialized form of slavery and the “race” concept and were introduced. With reference to the parallel development of slave societies by the English and Dutch in colonial America and at the Cape of Good Hope (South Africa), respectively, Fredrickson notes that these processes")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("were conditioned on the crucial assumption that nonwhites were enslavable while Europeans were not. This presumption is sometimes seen as evidence of a conscious racism—a belief that whites were destined by God or nature to rule over peoples whose physical characteristics denoted their innate inferiority. But . . . [t]he evidence strongly suggests that Africans and other non-Europeans were initially enslaved not so much because of their color and physical type as because of their legal and cultural vulnerability. . . . The combination of heathenness and "), _c('em', [_vm._v("de facto")]), _vm._v(" captivity was what made people enslavable, not their pigmentation or other physical characteristics."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en143"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en143",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("143")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("Therefore, it is “misleading and anachronistic to read the overt physical racism that emerged later back into the thought of this era.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en144"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en144",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("144")])]), _vm._v(" The available evidence also supports a more radical conclusion: that the very idea of distinct races, along with systematic racism, emerged largely in the transition from religious and cultural justifications for slavery to distinctly "), _c('em', [_vm._v("racial")]), _vm._v(" justifications for it.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The basic dynamics at work in the racialization of Negroes and whites in the seventeenth century were strikingly evident in the social, legal, and ideological shifts in the treatment of Negroes in England’s Virginia colony. By the 1630s, poor Englishmen were arriving annually in Virginia “in large numbers, engaged to serve the existing planters for a term of years with the prospect of setting up their own households a few years later.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en145"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en145",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("145")])]), _vm._v(" The colony’s representative assembly secured certain basic liberties of Englishmen for all European immigrants, who eventually included people "), _c('a', {
    attrs: {
      "id": "page_46"
    }
  }), _vm._v("(mostly men in the early years) who were French, Spanish, Dutch, Turkish, Portuguese, and African. From the start, the status of the Africans, who were few in number until about 1640, was somewhat distinct from that of English and other European immigrants of the servant and laboring classes. Still, there is little evidence that they were treated much differently from other servants until after 1660. According to Edmund Morgan, “It seems clear that most of the Africans, perhaps all of them, came as slaves, a status that had become obsolete in England, while it was becoming the expected condition of Africans outside Africa and a good many inside. It is equally clear that a substantial number of Virginia’s Negroes were free or became free. And all of them, whether servant, slave, or free, enjoyed most of the same rights and duties as other Virginians.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en146"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en146",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("146")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Virginia’s planter class began to change this situation after 1660, when perpetual hereditary slavery for Africans was first written into Virginia law. In 1669, African slaves were first designated as chattel rather than as persons whose labor was the property of their masters."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en147"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en147",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("147")])]), _vm._v(" Other components of racialized slavery emerged gradually after 1640. After 1640 there emerged “a growing number of freemen who had served their terms but who were unable to afford land of their own except on the frontiers or in the interior.” Some of them resigned themselves to working for others for wages, which was understood to be something less than a truly independent existence."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en148"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en148",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("148")])]), _vm._v(" This growing class of economically marginal English Virginians worried those “planters who had made it to the top or who had arrived in the colonies already on top, with ample supplies of servants and capital.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en149"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en149",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("149")])]), _vm._v(" This threat materialized in Bacon’s Rebellion of 1676. The largest popular uprising in the colonies before the American Revolution, it joined poor European Americans and African Americans in a fight to abolish servitude."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en150"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en150",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("150")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This "), _c('em', [_vm._v("class")]), _vm._v("-based alliance of European Americans and African Americans indicates that the notion of a “white race did not yet exist” in the American colonies."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en151"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en151",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("151")])]), _vm._v(" The Virginia planter class’s response to Bacon’s Rebellion, however, was instrumental in consolidating white and black racial identities. The planters recognized that they could not continually respond to discontent among a growing class of poor Englishmen with repression. Therefore, they began to rely much more fully on the labor of enslaved Africans, the cheapest source of labor available to them, which enabled them to avoid actively repressing other English settlers. “In the last years of the seventeenth century they bought [slaves] in such numbers that slaves probably constituted a majority or near majority of the labor "), _c('a', {
    attrs: {
      "id": "page_47"
    }
  }), _vm._v("force by 1700. . . . The increase in the importation of slaves was matched by a decrease in the importation of indentured servants and consequently a decrease in the dangerous number of new freedmen who annually emerged seeking a place in society that they would be unable to achieve.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en152"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en152",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("152")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Some commentators have questioned whether the planters’ increased reliance on slaves was part of a conscious strategy to avoid importing new indentured servants from Europe."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en153"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en153",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("153")])]), _vm._v(" A few facts are beyond dispute, however: the planters purchased increasing numbers of slaves from Africa after the 1670s, and by 1700 slaves constituted nearly a majority of the labor force of Virginia; there "), _c('em', [_vm._v("was")]), _vm._v(" a corresponding decrease in the importation of indentured servants; and, perhaps most significant, new laws in Virginia (and in other colonies) after 1670 expressly codified racialized (i.e., primarily Negro) slavery."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en154"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en154",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("154")])]), _vm._v(" For instance, Virginia’s second statutory definition of a slave, in 1682, “awkwardly attempted to rest enslavement on religious difference while excluding from possible enslavement all heathens who were not Indian or Negro.” This Virginia law (and others like it) left open the question of whether or not Indians or Negroes who converted to Christianity could be kept as slaves. By around the end of the century new laws in Maryland, New York, Virginia, North and South Carolina, and New Jersey answered this question affirmatively: they held that masters were not required free slaves who had become Christians."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en155"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en155",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("155")])]), _vm._v(" A whole series of laws passed in Virginia between 1668 and 1723, including the slave code of 1705, testified to the planter class’s aim “not only to impose lifetime hereditary bond-servitude on African-Americans, but to implement it by a system of "), _c('em', [_vm._v("racial oppression,")]), _vm._v(" expressed in laws against "), _c('em', [_vm._v("free")]), _vm._v(" African-Americans.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en156"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en156",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("156")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In the second half of the seventeenth century the gradual racialization of laws and social relations in American colonies was also expressed in new laws that prohibited sexual relations and marriage between English colonists and Negroes. In 1662, the Virginia assembly doubled the usual fine for “fornication” in the case of “any Christian [who] shall committ Fornication with a negro man or women”; in 1663, Bermuda prohibited “all sexual relations between whites and Negroes”; a 1664 Maryland law banned marriages of “freeborne English women forgetfull of their free Condicion . . . with Negro slaves”; and a 1681 Maryland law described marriages between “white” women and “Negroes” as “always to the Satisfaction of theire Lascivious and Lustful desires, and to the disgrace not only of the English butt allso of many other Christian nations.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en157"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en157",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("157")])]), _vm._v(" In 1692, "), _c('a', {
    attrs: {
      "id": "page_48"
    }
  }), _vm._v("a revised Maryland law against miscegenation dropped the term "), _c('em', [_vm._v("Christian")]), _vm._v(" but retained reference to “"), _c('em', [_vm._v("white")]), _vm._v(" and "), _c('em', [_vm._v("English.")]), _vm._v("”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en158"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en158",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("158")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The racialization of slavery thus involved the de facto invention of a pan-European “white race.” In the course of the seventeenth century, English colonizers in North America gradually accepted that the colonies would not remain exclusively English. Virginia enacted legislation in 1671 to support the naturalization of all legal aliens so that they would gain “all such liberties, privileges, immunities whatever, as a naturall borne Englishman is capable of.” Maryland adopted similar provisions."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en159"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en159",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("159")])]), _vm._v(" Scottish and (especially) Irish servants were sometimes treated harshly, but they were never reduced to a condition of “perpetual slavery,” a condition that was reserved for Negroes and, occasionally, Indians."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en160"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en160",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("160")])]), _vm._v(" As a result, the institutionalization of racialized slavery in the American colonies in the late seventeenth and early eighteenth centuries joined all the European settlers—regardless of national origins or social class—as members of one white race, at least for the time being (see "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch04#ch04",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 4")]), _vm._v("). Segal explains, “the rendering of ‘Africans’ as a singular race-class [i.e., both Negroes and slaves] meant that settlers from Europe—their class diversity notwithstanding—became a singular race.” The rigid European status hierarchy produced by feudalism was exteriorized in the American colonies; that is, “hierarchy was inscribed solely beyond the boundary of what became the collective, racial self—‘the white [person].’”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en161"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en161",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("161")])]), _vm._v(" The term "), _c('em', [_vm._v("white")]), _vm._v(" came into wide use across England’s American colonies after about 1680, which marked a further step in the elaboration of racial discourse and race consciousness."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en162"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en162",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("162")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This creation of the white race had a further notable effect, particularly in England’s American colonies: it tended to obscure the significance of class differences among those people who were now called “white.” Basically, the creation of racialized distinctions produced a kind of “aristocracy of the skin” for those included in the white race."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en163"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en163",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("163")])]), _vm._v(" This racialized “ennoblement,” however, was different from that characteristic of the earlier Europe feudal hierarchies: “white” racialized identity, like ennoblement within the "), _c('em', [_vm._v("ancien regime,")]), _vm._v("“was a basis for claiming full political rights, for claiming to be subordinate to none.” At the same time, unlike the hereditary status hierarchy of feudalism, the new racialized status hierarchy was embedded within an emerging class structure that promised opportunities for equal citizenship, economic and political independence, and social mobility but reserved these prerogatives exclusively for members of the white race (mainly for white "), _c('em', [_vm._v("men")]), _vm._v(")."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en164"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en164",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("164")])]), _vm._v(" Distinctions between propertied and propertyless whites as well as between large- and smallscale "), _c('a', {
    attrs: {
      "id": "page_49"
    }
  }), _vm._v("property holders were central to the emerging system of "), _c('em', [_vm._v("class")]), _vm._v(" stratification. Basic to the new "), _c('em', [_vm._v("racialized")]), _vm._v(" status hierarchy was a distinction between the poorest member of the dominant racialized group (i.e., the white race) and all members, however propertied, of the oppressed racialized groups."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en165"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en165",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("165")])]), _vm._v(" Moreover, the new system of racialized domination secured a virtual monopoly of political power and material resources and opportunities for members of the white race."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en166"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en166",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("166")])]), _vm._v(" Yet, for lower-class members of the white race, the new racialized order of “white skin privilege” worked to conceal constraints to social mobility that were endemic to the rising class structure.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("There were similar patterns of racialization in Portuguese and Dutch colonies. In the fifteenth and sixteenth centuries, Portuguese and Dutch colonial encounters were shaped by their sense of their religious and cultural superiority compared to the peoples of Africa, Asia, and the Americas. By the end of the seventeenth century, Dutch and Portuguese colonialists increasingly used racial language, such as Portuguese references to “Asiatic races.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en167"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en167",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("167")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Ultimately, the enslavement of Africans in the Americas by European elites in the sixteenth and seventeenth centuries generated not only a distinctly racialized form of slavery but also modern racism and the concept of race. As David Brion Davis says, “[S]lavery produced racism, in the sense that the negative stereotypes that had been applied to slaves and serfs since antiquity, regardless of ethnicity, were ultimately transferred to black slaves and then to most people of African descent after bondage became almost exclusively confined to blacks.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en168"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en168",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("168")])]), _vm._v(" The institutionalized enslavement of Africans also contributed to the development of the concept of race. That is, in the process of codifying and rationalizing racialized slavery, European elites—especially the English colonial planter class—spurred the crystallization of the modern “race” concept. Armed with this concept, European elites created a new system of social stratification based on the idea that human beings are divisible into distinct types (i.e., races) comprising inherited physical characteristics that correspond to different and unequal characters and abilities. All that remained for the full elaboration of the concept of race was for intellectuals and scientists working in the fields of “natural history” and biology to use the term to classify supposedly distinct types of human beings in a systematic way.")]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('a', {
    attrs: {
      "id": "page_50"
    }
  }), _c('strong', [_c('em', [_vm._v("The Beginnings of Modern Scientific Racialism")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Efforts to classify human “races” were a by-product of how new ideas about race in the late seventeenth and early eighteenth centuries were incorporated into the broader project to classify the natural world. The latter was part of the modern Scientific Revolution. The development of the field of natural history in the seventeenth century was an important part of this process. It gave special importance to visible markers of similarity and difference among the myriad parts of nature."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en169"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en169",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("169")])]), _vm._v(" In the sixteenth and early seventeenth centuries, books about the natural world bore titles such as "), _c('em', [_vm._v("History of the Nature of Birds")]), _vm._v(" (Belon) and "), _c('em', [_vm._v("Admirable History of Plants")]), _vm._v(" (Duret); in 1657, Joannes Jonston published his "), _c('em', [_vm._v("Natural History of Quadrupeds,")]), _vm._v(" marking the onset of a new way of conceiving of nature historically."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en170"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en170",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("170")])]), _vm._v(" Natural history addressed the place of human beings along with plants, animals, and minerals in the natural order of things. It involved a process of naming and classifying living creatures to establish a descriptive order that clarified the various relationships of affinity and difference among them."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en171"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en171",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("171")])]), _vm._v(" This new scientific enterprise included work by John Ray and Joseph de Tournefort in the 1680s and 1690s to establish new criteria for classifying plants and animals, and the comparative anatomist Edward Tyson’s 1699 argument that the orangutan (his “pygmy”) was an “intermediate Link between Ape and Man” in the “Chain of Creation.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en172"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en172",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("172")])]), _vm._v(" This classificatory impulse culminated in the Swedish botanist Carolus Linnaeus’s "), _c('em', [_vm._v("System of Nature")]), _vm._v(" (1735)—a comprehensive classification of the hierarchy of being that encompassed minerals, nonhuman animals, and human beings. With Linnaeus, mature natural history of the eighteenth century also sought to classify distinct “varieties” (or races) of human beings (see "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch02#ch02",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 2")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The natural history endeavor to distinguish and classify races of human beings was partially prefigured by a few sixteenth- and seventeenth-century writers on anatomy and nature such as Andreas Vesalius (1514–64) and William Petty (1623–87). Vesalius, a Flemish founder of the modern science of anatomy, advanced claims about the relationship between human skull shapes and national origins that anticipated later racialist craniology. “It seems that certain nations,” he wrote,")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("have something peculiar in the shape of the head. The heads of the Genoese, and more particularly of Greeks and Turks, almost exhibit a round shape. To this also (which not a few of them think elegant and consider to "), _c('a', {
    attrs: {
      "id": "page_51"
    }
  }), _vm._v("be well adapted to the turbans which they use in various ways) the mid-wives sometimes contribute at the urgent request of the mother. The Germans, indeed, have a very flattened occiput and a broad head, because the boys always lie on their backs in their cradles."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en173"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en173",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("173")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("Vesalius combined comparative anatomy with an interest in the different customs or ways of life of different nations. Therefore, his approach was distinct from later racialist thought, especially insofar as he regarded the varying head shapes as a consequence of different national customs rather than vice versa (see "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch02#ch02",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapters 2")]), _vm._v(" and "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch03#ch03",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The thought of Petty—English political economist, adventurer, physician, statistician, professor of anatomy and music, and follower of Francis Bacon’s “new philosophy”—was closely related to scientific racialism."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en174"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en174",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("174")])]), _vm._v(" Petty, a strong advocate for English colonization of Ireland in this era, employed his talent for political economy and statistics in a 1662 proposal that slavery be instituted for “insolent Thieves” in England."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en175"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en175",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("175")])]), _vm._v(" He also articulated basic features of the emerging racialist natural history of the late seventeenth century. In 1676–77, he put forward a rudimentary account of distinct human races, which he conceived within a broader theory of “two Scales of animate beings”—that is, “of beings which act by souls.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en176"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en176",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("176")])]), _vm._v(" He built upon the notion of a “Great Chain of Being,” which held that all beings—from the simplest to the highest creatures, human beings—were linked to each other and to God in a fixed, hierarchical order. Each type of being was categorically distinct from the others, and there was no sense that any higher beings may have developed from any lower order of beings."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en177"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en177",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("177")])]), _vm._v(" In the “higher” of his two scales of being, Petty saw a theistic order, with “the maker of the . . . visible world [at] the top and Man at the bottom.” In his “lower” scale of earthly beings, he placed human beings, distinguished by capacities for speech and reason, on the top of the scale, with its bottom made up of “the smallest and simplest animall that man can discern.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en178"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en178",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("178")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The “Lower Scale” encompassed “another Scale containing the Severall Stepps and gradations of improuvements which man hath made from the lowest and simplest condition that mankind was ever in, unto the highest.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en179"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en179",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("179")])]), _vm._v(" There were several “species” or races of human beings, and the differences in size between the largest and smallest of them—“Gyants and Pigmyes”—corresponded to differences “also in the Memories, Witts, Judgments & withall in their external senses.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en180"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en180",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("180")])]), _vm._v(" Along with these differences,")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_c('a', {
    attrs: {
      "id": "page_52"
    }
  }), _vm._v("there bee others more considerable, that is, between the Guiny Negros & the Middle Europeans; and of the Negros between those of Guiny and those who live about the Cape of Good Hope, which last are the Most beastlike of all the Souls [? Sorts] of Men whom our Travellers are well acquainted. I say that the Europeans do not onely differ from the aforementioned Africans in Collour, which is as much as white differs from black, but also in their Haire which differs as much as a straight line differs from a circle; but they also differ in the shape of their Noses, Lipps and cheek bones, as also in their skulls. They differ also in their Naturall Manners, & in the internall Qualities of their Minds.")]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("Petty added that the peoples who lie in the “Northernmost parts of the Habitable world,” including the Laplanders, “are a very mean sort of Men, both in their Statures and understanding.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en181"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en181",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("181")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Seven years after Petty’s speculations, the French physician François Bernier (1620–88) was the first writer to use the term "), _c('em', [_vm._v("race")]), _vm._v(" in something like the modern sense of “a major division of humanity displaying a distinctive combination of physical traits transmitted through a line of descent.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en182"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en182",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("182")])]), _vm._v(" Bernier, who visited Poland, the Near East, and southwest Asia, including Palestine, Syria, Egypt, and India, became famous in France as a travel writer. He served for several years as a physician in the court of the “Grand Mogul” Aurangzeb in Agra (Uttar Pradesh) and traveled to Kashmir and Lahore. He later returned to France by way of Bengal, Surat (on India’s west coast), and Persia and wrote about his travels in "), _c('em', [_vm._v("Histoire de la dernière révolution des états du Grand Mogol")]), _vm._v(" (1670–71)."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en183"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en183",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("183")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Based on his travels, Bernier anonymously proposed his racial classification, “A New Division of the Earth according to the Different Species or Races of Men,” in the French "), _c('em', [_vm._v("Journal des Scavans")]), _vm._v(" in 1684. He did not use the term "), _c('em', [_vm._v("race")]), _vm._v(" precisely or address how his human “races” fit into the broader scheme of nature in the manner of the major eighteenth-century theorists of race, Linnaeus, Buffon, Kant, and Blumenbach."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en184"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en184",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("184")])]), _vm._v(" Still, Bernier’s scheme marked a break among European writers from “sacred history” of human beings, which relied on the Bible, to natural history (although many raciologists continued to use biblical assumptions implicitly); and his text seems to have had a significant influence on the racial categories that Blumenbach and others later elaborated. Blumenbach himself read and cited Bernier, and the boundaries of Bernier’s notion of a European-centered but not exclusively European race largely anticipated the geographical boundaries of Blumenbach’s Caucasian race (see "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch02#ch02",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 2")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_53"
    }
  }), _vm._v("Bernier divided the world’s peoples into “four or five species or races of men . . . whose difference is so remarkable that it may be properly made use of as the foundation for a new division of the earth.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en185"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en185",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("185")])]), _vm._v(" He used the term "), _c('em', [_vm._v("species")]), _vm._v(" ("), _c('em', [_vm._v("espèces")]), _vm._v(") loosely and more or less interchangeably with the term "), _c('em', [_vm._v("race,")]), _vm._v(" but he was a "), _c('em', [_vm._v("monogenist")]), _vm._v("—a believer in the basic unity and common origins of all human beings—who did not regard his races as separate biological species."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en186"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en186",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("186")])]), _vm._v(" Therefore, where he spoke of different species of human beings his meaning is best understood in terms of the nascent “race” concept. Bernier’s first “race” (he did not name his races) included the people of")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("France, Spain, England, Denmark, Sweden, Germany, Poland, and generally all Europe, except a part of Muscovy. To this may be added a small part of Africa, that is from the kingdoms of Fez, Morocco, Algiers, Tunis, and Tripoli up to the Nile; and also a good part of Asia, as the empire of the Grand Seignior with the three Arabias, the whole of Persia, the states of the Grand Mogul, the Kingdom of Golconda, that of Visapore, the Maldivias, and a part of the kingdoms of Araucan, Pegu, Siam, Sumatra, Bantan and Borneo."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en187"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en187",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("187")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("His rationale for including in this first race the northern part of Africa and “a good part of Asia” (mainly western and southern regions) along with most of Europe is telling, especially when we consider more fully his classificatory criteria:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("[A]lthough the Egyptians, for instance, and the Indians are very black, or rather copper-coloured, that colour is only an accident in them, and it comes because they are constantly exposed to the sun; for those individuals who take care of themselves, and who are not obliged to expose themselves so often as the lower class, are not darker than many Spaniards. It is true that most Indians have something very different from us in the shape of their face, and in their colour which often comes very near yellow; but that does not seem enough to make them a species apart, or else it would be necessary to make one of the Spaniards, another of the Germans, and so on with the several nations of Europe."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en188"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en188",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("188")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("In short, while these various peoples differed somewhat in skin tone and in facial shapes, these differences were only superficial. In particular, those among them with darker skin tones—Egyptians and Indians—were not "), _c('a', {
    attrs: {
      "id": "page_54"
    }
  }), _vm._v("significantly darker than the other peoples; their evident darkness was merely “accidental.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Significantly, Bernier supported this point with an appeal to "), _c('em', [_vm._v("class")]), _vm._v(" differences: his remark that those Egyptians and Indians “who are not obliged to expose themselves [to the sun] so often as the lower class are not darker than many Spaniards” is indicative of the ways in which claims about “racial” affinities or differences between groups frequently have been confused with, or served as tropes for, "), _c('em', [_vm._v("real")]), _vm._v(" (but changeable) differences of class, ethnicity, culture, or religion."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en189"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en189",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("189")])]), _vm._v(" His remark echoed common symbolism in medieval Europe concerning peasants, who constituted the vast majority of the population. They were often regarded contemptuously by the aristocracy, clergy, and commercial classes, even though these elites relied on their labor. Because of this duality of elite distance from and dependence on the peasants, medieval European images depicted peasants as filthy, stupid, and bestial but also as pious and close to God. As I noted earlier, European elites also appealed to the biblical curse on Ham’s son Canaan to explain and justify the subordination of peasants. In Bernier’s native France, moreover, serfs and peasants were “often depicted as dark-skinned or ‘black,’ either by reason of their labor in the sun and their proximity to the earth, or as a sign of their overall hideousness.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en190"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en190",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("190")])]), _vm._v(" Such ideas are a likely source for the later theories that "), _c('em', [_vm._v("upper-caste")]), _vm._v(" Hindus (but not all Hindus) were members of the Caucasian race.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Bernier’s understanding of racial difference becomes clearer when we turn to his typology of four other races. His “second species” encompassed the peoples who inhabited the "), _c('em', [_vm._v("rest")]), _vm._v(" of Africa:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("1. Their thick lips and squab noses, there being very few among them who have aquiline noses or lips of moderate thickness. 2. The blackness which is peculiar to them, and which is not caused by he sun, as many think. . . . The cause must be sought for in the peculiar textures of their bodies, or in the seed, or in the blood—which last are, however, of the same color as everywhere else. 3. Their skin, which is oily smooth, and polished, excepting the places which are burnt with the sun. 4. The three or four hairs of beard. 5. Their hair, which is not properly hair, but rather a species of wool . . . and, finally, their teeth whiter than the finest ivory, their tongue and all the interior of their mouth and their lips as red as coral."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en191"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en191",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("191")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent1"
  }, [_vm._v("He delineated more succinctly the distinguishing characteristics of his two other races. The third included the peoples in “part of the kingdoms of "), _c('a', {
    attrs: {
      "id": "page_55"
    }
  }), _vm._v("Araucan and Siam, the islands of Sumatra and Borneo, the Philippines, Japan, the kingdom of Pegu, Tonkin, Cochin-China, China, Chinese Tartary, Georgia . . ., the Usbek, Turkistan, Zaquetay, and a small part of Muscovy, the little Tartars and Turcomans who live along the Euphrates towards Aleppo.” All these people “are truly white; but they have broad shoulders, a flat face, a small squab nose, little pig’s eyes long and deep set, and three hairs of beard.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en192"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en192",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("192")])]), _vm._v(" The Laplanders (“Lapps”) made up his fourth race. They were “thick” and “stunted creatures” with “a face immensely elongated; very ugly and partaking much of the bear,” although he admitted to having only limited experience with them."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en193"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en193",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("193")])]), _vm._v(" Finally, he considered but rejected the thought that indigenous Americans constituted a fifth “species” or “race.” Most of them were “olive-coloured, and their faces modelled in a different way from ours. Still I do not find the difference sufficiently great to make of them a peculiar species different from ours.” To support this claim he added that as among Europeans there were great differences among native Americans in “stature, the turn of the face, the color and the hair.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en194"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en194",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("194")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In light of later efforts at racial classification, a few interrelated features of Bernier’s typology are especially noteworthy. First, in contrast to some later classifications, his European-centered race included peoples from noncontiguous (and rather widely scattered) areas. He divided peoples from several kingdoms—Araucan, Pegu, Siam, Sumatra, Bantan, and Borneo—into two separate races, and he included Georgians (of the Caucasus) and part of Muscovy (Russia) in his third race rather than his first. The impetus for these moves appears to stem from the two criteria with which he distinguished his first species (or race) from his four others: a Eurocentric aesthetic of “beauty” or “handsomeness” and the presence or absence of the accoutrements of “civilization” as Europeans understood them. Bernier used the aesthetic criteria—chiefly facial features, skin color, and stature—explicitly and the “civilizational” standard only implicitly. Indeed, with a couple of exceptions he refrained from speculating about any intrinsic moral or intellectual traits."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en195"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en195",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("195")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("With regard to his aesthetic criteria, while Bernier’s first race was not exclusively European, he included other peoples in it (Indians and Egyptians among them; native Americans more provisionally) insofar as they looked like Europeans."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en196"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en196",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("196")])]), _vm._v(" Moreover, he not only listed this race first but also made it the standard against which he measured the others."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en197"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en197",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("197")])]), _vm._v(" He discussed aesthetic differences mostly with respect to women, saying that “beautiful and ugly ones are found everywhere”; yet he judged beauty by a "), _c('a', {
    attrs: {
      "id": "page_56"
    }
  }), _vm._v("standard that favored “aquiline noses” and thin lips that were more common to the peoples who make up his first race. Thus, he noted several “very handsome” women among “the blacks of Africa, who had not those thick lips and that squab nose” characteristic of most of black Africans."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en198"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en198",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("198")])]), _vm._v(" He spoke of “beautiful brunettes” in the Indies and women of Cashmere who were especially striking, “for besides being as white as those of Europe, they have a soft face, and are a beautiful height.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en199"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en199",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("199")])]), _vm._v(" And, foreshadowing another feature of Blumenbach’s classification scheme, he mentioned the handsomeness of people from the Caucasus (“handsome slaves who are brought [to Persia] from Georgia and Circassia”)."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en200"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en200",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("200")])])]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('strong', [_c('em', [_vm._v("“Race” and Racialism, circa 1684")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Bernier’s racial classification typified the new way of “knowing” human life that was introduced by natural history and modern racialism. This naturalist view challenged existing sacred and theistic conceptions of human nature and history, notably the biblical viewpoint. In addition, Bernier’s specific scheme of four (or five) distinct races of human beings (and others that followed it) represented a big step away from what C. Loring Brace calls “the peasant perspective” on human diversity. From this Old World perspective, “the normal range of human experience was limited to what could be seen in that segment of the world lying within a radius of 25 miles from the place of one’s birth”; various human groups were “essentially the same” in appearance, and the perceived differences between groups of people tended to concern “custom and dress and traditions of behavior and speech.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en201"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en201",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("201")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The new discernment of racial differences coincided with the intensification of long-distance oceangoing travel by Europeans. This put Europeans into contact with peoples who visibly differed from them not only culturally and technologically but also in physical appearance. European travelers’ reports from distant lands produced a composite picture of human physical differentiation that overshadowed the actual gradation of physiognomic characteristics from one localized human community to the next—something that was evident to such earlier observers as Herodotus, Marco Polo, and Ibn Batutah, who traveled mostly overland."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en202"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en202",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("202")])]), _vm._v(" Moreover, the way in which Europeans dominated these new contacts with Africans, Asians, and Americans fueled their religiously and culturally based sense of their own “innate” superiority.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_57"
    }
  }), _vm._v("By the end of the seventeenth century, against the backdrop of the Atlantic slave trade, European colonial expansion, and a growing tendency for Europeans to perceive native Americans as fundamentally different from themselves, European naturalists had begun to use “race” and “racial classification” to give these prejudices scientific credence."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch1en203"
    }
  }), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/notes#ch1.en203",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("203")])]), _vm._v(" These prejudices also informed their science. Racial classifiers such as Bernier and those who followed his initiative during the next century (Linnaeus, Buffon, Kant, Blumenbach) were predisposed toward racial classifications that placed “white” peoples such as themselves above “nonwhite” peoples. Thus they began to align science behind new ideas of white supremacy. Simultaneously, Bernier’s emphasis on perceived physical similarities and differences between people led him to refine Eurocentric racialism in an influential way. He included not just nearly “all Europe” but also parts of Africa and Asia in his first (“white”) race. (Recall that he also describes his third race as “truly white.”) Linnaeus (among others) would later reject this move in favor of a (“white”) European “variety” of "), _c('em', [_vm._v("homo sapiens")]), _vm._v("; but Blumenbach would incorporate it into his “Caucasian race” category (see "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch02#ch02",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 2")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Meanwhile, the period of European history from roughly 1000 to 1684 left another formative racial legacy: enduring social, political, ethnic, and religious cleavages "), _c('em', [_vm._v("within")]), _vm._v(" Europe that had influenced the development of racial thought. These divisions—including the perceived otherness of Jews, Muslims, Slavs, and the Irish, along with presumed distinctions between Gauls and Franks in France and Normans and Saxons in England—were not initially construed as racial distinctions. These divisions shaped subsequent debates about the racial identity of Europe’s Jews, however, and in relation to the rise of nationalism and other social changes in the second half of the nineteenth century, such divisions were routinely given racial meaning by European race scientists (see "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch04#ch04",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 4")]), _vm._v(").")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-c1c385f8","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/ch01.vue
// module id = 81
// module chunks = 0